import {useRef, useState} from "react";
import {
    DigitalSigningFailure,
    DigitalSigningFailureReason,
    DigitalSigningSignRequest,
    DigitalSigningTipRequest
} from "./digitalSigning.types";
import {isDigitalSigningFailure, submitSignature} from "./publicDigitalSigning.api";

interface Options {
    token: string
    snapshotSha256: string
    language?: string
    onSigned: () => void
    onGone: (reason: DigitalSigningFailureReason) => void
}

/**
 * Sends the tip and the drawn signature, only when the guest taps: a failure is shown with "Try
 * again", never retried or queued in the background.
 */
export const useSigningSubmit = ({token, snapshotSha256, language, onSigned, onGone}: Options) => {
    const [submitting, setSubmitting] = useState(false);
    const [failure, setFailure] = useState<DigitalSigningFailure | undefined>(undefined);
    const [failedAttempts, setFailedAttempts] = useState(0);

    const submittingRef = useRef(false);
    // A lost response may still have recorded this exact request, including the consent language.
    const lostPayloadRef = useRef<string>();

    const submit = async (tip: DigitalSigningTipRequest, signaturePng: string) => {
        if (submittingRef.current) return;

        submittingRef.current = true;
        setSubmitting(true);
        setFailure(undefined);
        const request: DigitalSigningSignRequest = {token, snapshotSha256, tip, signaturePng, language};
        const payload = JSON.stringify(request);

        try {
            await submitSignature(request);
            onSigned();
        } catch (error) {
            const next: DigitalSigningFailure = isDigitalSigningFailure(error) ? error : {reason: 'NETWORK'};

            // USED acknowledges a lost attempt only when all the signed evidence still matches.
            if (next.reason === 'USED' && lostPayloadRef.current === payload) {
                onSigned();
                return;
            }
            if (next.reason !== 'NETWORK' && next.reason !== 'INVALID') {
                onGone(next.reason);
                return;
            }

            if (next.reason === 'NETWORK') lostPayloadRef.current = payload;
            setFailure(next);
            setFailedAttempts(count => count + 1);
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    };

    // A new tip and signature are a new attempt: the error and its count start over
    const resetFailures = () => {
        setFailure(undefined);
        setFailedAttempts(0);
    };

    return {submit, submitting, failure, failedAttempts, resetFailures};
};
